import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import DocumentoClienteFields from '../common/DocumentoClienteFields';
﻿import React, { useEffect, useState } from 'react';
import { X, UserPlus } from 'lucide-react';
import { fetchUserLevels } from '../../services/userLevelService';
import { registerUser } from '../../services/userService';
import { createCliente, updateCliente, fetchClientes, Cliente } from '../../services/clienteService';
import ImageUpload from '../Upload/ImageUpload';
import DeclaracionJuradaUpload from '../Upload/DeclaracionJuradaUpload';

interface AddClienteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddCliente: (nuevoCliente: Cliente) => void;
}

interface UserLevel {
    id: number;
    nombre: string;
    perfil_asociado: 'cliente' | 'personal';
    activo: boolean;
}

const AddClienteModal: React.FC<AddClienteModalProps> = ({ isOpen, onClose, onAddCliente }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        estado: true,
        tipo_nivel_usuario: '',
        telefono: '',
        ci_nit: '', codigo_tipo_documento_identidad_sin: 1, complemento: '',
        foto: null as File | null,
        foto_declaracion_jurada: null as File | null,
    });

    const [userLevels, setUserLevels] = useState<UserLevel[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Resetear formulario y cargar niveles al abrir
    useEffect(() => {
        if (isOpen) {
            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                estado: true,
                tipo_nivel_usuario: '',
                telefono: '',
                ci_nit: '', codigo_tipo_documento_identidad_sin: 1, complemento: '',
                foto: null,
                foto_declaracion_jurada: null,
            });
            setError(null);
            const loadUserLevels = async () => {
                try {
                    const levels = await fetchUserLevels();
                    const clienteLevels = levels.filter((level: UserLevel) => level.perfil_asociado === 'cliente');
                    setUserLevels(clienteLevels);
                } catch (err) {
                    /* Browser diagnostics omitted. */
                    setError('No se pudieron cargar los niveles de usuario');
                }
            };
            loadUserLevels();
        }
    }, [isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !formData.password.trim()) {
            setError('Nombre, apellido, email y contraseña son obligatorios');
            return;
        }

        setLoading(true);

        try {
            // 1. Crear usuario
            const userData = await registerUser(
                formData.email,
                formData.first_name,
                formData.last_name,
                formData.password,
                formData.estado,
                formData.tipo_nivel_usuario ? parseInt(formData.tipo_nivel_usuario) : null
            );

            const userId = userData.id;

            // 2. Verificar si ya existe registro de cliente para este usuario
            const clientes = await fetchClientes();
            const clienteExistente = clientes.find(c => c.usuario?.id === userId);

            let clienteFinal: Cliente;

            if (clienteExistente) {
                // Actualizar cliente existente con datos y foto de declaracion jurada
                clienteFinal = await updateCliente(clienteExistente.id, {
                    telefono: formData.telefono || null,
                    ci_nit: formData.ci_nit || null, codigo_tipo_documento_identidad_sin: formData.codigo_tipo_documento_identidad_sin, complemento: formData.complemento,
                    foto: formData.foto,
                    foto_declaracion_jurada: formData.foto_declaracion_jurada,
                    declaracion_jurada_firmada: Boolean(formData.foto_declaracion_jurada),
                });
            } else {
                // Crear nuevo cliente
                clienteFinal = await createCliente({
                    usuario: userId,
                    telefono: formData.telefono || null,
                    ci_nit: formData.ci_nit || null, codigo_tipo_documento_identidad_sin: formData.codigo_tipo_documento_identidad_sin, complemento: formData.complemento,
                    foto: formData.foto,
                    foto_declaracion_jurada: formData.foto_declaracion_jurada,
                    declaracion_jurada_firmada: Boolean(formData.foto_declaracion_jurada),
                });
            }

            onAddCliente(clienteFinal);
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.message || 'Error al crear el cliente. Verifica los datos.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />
            <LumaAccessiblePanel coworkTitle="Crear · Clientes" data-luma-dialog="true" className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-start mb-4">
                    <LumaAccessibleTitle className="text-xl font-bold text-gray-900">Agregar Nuevo Cliente</LumaAccessibleTitle>
                    <div className="flex items-center gap-4">
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <button 
                            onClick={onClose} 
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        {/* Nombre */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nombre <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Apellido */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Apellido <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Contraseña */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contraseña <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Nivel de usuario (solo clientes) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nivel de usuario
                            </label>
                            <select
                                name="tipo_nivel_usuario"
                                value={formData.tipo_nivel_usuario}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Seleccionar nivel</option>
                                {userLevels.map(level => (
                                    <option key={level.id} value={level.id}>
                                        {level.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Teléfono */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                            <input
                                type="text"
                                name="telefono"
                                value={formData.telefono}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* CI/NIT */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">CI/NIT</label>
                            <input
                                type="text"
                                name="ci_nit"
                                value={formData.ci_nit}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <DocumentoClienteFields codigo={formData.codigo_tipo_documento_identidad_sin} complemento={formData.complemento} onChange={(codigo, complemento) => setFormData({ ...formData, codigo_tipo_documento_identidad_sin: codigo, complemento })} className="w-full border rounded-lg px-3 py-2" />

                    {/* Declaración Jurada y Firma Fotográfica */}
                    <div className="mb-4">
                        <DeclaracionJuradaUpload
                            setFile={(file) => setFormData(prev => ({ ...prev, foto_declaracion_jurada: file }))}
                        />
                    </div>

                    {/* Foto de perfil (opcional) */}
                    <div className="mb-4">
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                            Foto de perfil (opcional)
                        </label>
                        <ImageUpload setImagen={(file) => setFormData({ ...formData, foto: file })} />
                    </div>

                    {/* Toggle para Usuario activo */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">
                                    Usuario activo
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.estado ? 'El usuario estará habilitado' : 'El usuario estará inhabilitado'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="estado"
                                    className="sr-only"
                                    checked={formData.estado}
                                    onChange={handleChange}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${
                                        formData.estado ? 'bg-[#3564db]' : 'bg-gray-300'
                                    }`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${
                                            formData.estado ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    ></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 text-sm bg-[#3564db] text-white rounded-lg transition-colors font-medium flex items-center ${
                                loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#8f1f64]'
                            }`}
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Guardando...
                                </span>
                            ) : (
                                'Guardar Cliente'
                            )}
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

export default AddClienteModal;

